import { baseInventoryIds, type BaseEnvelope, type BasePatchOp } from './base-patch';
import type { AppliedFrame } from '../transport/applied-frame';
import { applyAppliedFrame } from '../transport/apply-applied-frame';
import { encounterCreatureField, encounterField } from '../../shared/array-types';
import { fromBaseRecord, type FromBaseRow as Row } from './from-base-context';
import { readItemField } from './item-field-reader';
import { readStatusCasterId, storedStatusRows } from './creature-status-reader';
import { savedResourceRows, applyResourceValues, type ResourceValues } from '../resources/resource-adjust';
import { relocationDefinitionsUnchanged, savedCreatureRelocationFrame } from './saved-creature-relocation';
import { recompileMemberStatusDiff, applyMemberStatusRoots, type MemberStatusStep } from './member-status-recompile';
import { observeActionComputed } from './action-computed-delta';
import { statusActivityFields } from './status-layering';
import { fromBaseStatusCampaignSeconds } from './from-base-status-calendar';
import { statusProjectionDependencies } from './status-attack-budget';
import { createStatusLayerReadContext } from './status-row-construction';
import { C1_CONTRACT } from './possessor-compile';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const memberId = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
const statusId = (row: Row): string => upper(readItemField(row, 'status_id'));
const statusItem = (row: Row): string => upper(readItemField(row, 'status_item_id'));
const RESOURCE_COLUMNS = ['v_current', 'v_max', 'v_temp', 'v_max_temp', 'aps'] as const;
const RESOURCES = new Set<string>(RESOURCE_COLUMNS);
const activityMetadata = { statusIds: Object.fromEntries(C1_CONTRACT.vitality.statusCatalog.rows
    .map(row => [row.variable_name, row.item_id])) };
const activityIds = new Set(['gathering', 'reading', 'training'].map(kind => upper(activityMetadata.statusIds[kind])));

/** Saved payloads mix statuses and resources with floor replacement.
 * Reuse their native differs; never compile their enclosing encounter. */
export async function savedPayloadMemberFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown): Promise<AppliedFrame | null> {
    if (ops.some(op => !['items', 'item_layer_types', 'statuses', 'encounter_creatures'].includes(op.table))
        || !fromBaseRecord(computed) && envelope.entity !== 'inventory'
        || !await relocationDefinitionsUnchanged(envelope.base, ops)) return null;
    const writes = ops.filter(op => ['statuses', 'encounter_creatures'].includes(op.table));
    if (!writes.length) return null;
    const eid = upper(encounterField(envelope.base, 'encounter_id') ?? encounterField(envelope.base.encounter_context, 'encounter_id')
        ?? encounterField(envelope.base.possessor, 'encounter_id'));
    const frame: AppliedFrame = { kind: 'applied', operation_id: `saved-payload:${envelope.entity}:${upper(envelope.entity_id)}:${ops[0]!.base_rev}`,
        encounter_id: eid, actor_id: '', rev: 0, member_revs: {}, rows: [], computed: {}, computed_delta: {}, new_entities: [], deletes: [] };
    // The floor inventory facet does not own creature statuses or creature vitality.
    if (envelope.entity === 'inventory') {
        if (eid !== upper(envelope.entity_id)) return null;
        const owners = new Set([eid, ...baseInventoryIds(envelope.base)]);
        return writes.every(op => op.table === 'encounter_creatures'
            || op.op === 'upsert' && !owners.has(upper(readItemField(op.cols, 'status_target_id')))
                && !owners.has(upper(readStatusCasterId(op.cols)))) ? frame : null;
    }
    const source = computed as Row;
    const members = [source, ...(Array.isArray(source.encounter_creatures_arr) ? source.encounter_creatures_arr.filter(fromBaseRecord) : [])]
        .filter(row => memberId(row));
    const rawMembers = [envelope.base, ...(Array.isArray(envelope.base.encounter_creatures_arr)
        ? envelope.base.encounter_creatures_arr.filter(fromBaseRecord) : [])];
    const catalog: Row = { ...(fromBaseRecord(envelope.base.catalog) ? envelope.base.catalog : {}),
        ...(fromBaseRecord(source.catalog) ? source.catalog : {}) };
    const incoming: Row = {};
    for (const op of ops) if (['items', 'item_layer_types'].includes(op.table)) {
        const id = upper(op.row_id);
        if (op.op !== 'upsert') return null;
        const definition = fromBaseRecord(incoming[id]) ? incoming[id] : {};
        incoming[id] = { ...definition, ...op.inputs, [op.table]: op.cols };
    }
    for (const [id, definition] of Object.entries(incoming)) if (!(id in catalog)) catalog[id] = definition;
    frame.inputs = { catalog: incoming };
    const creatureOps = writes.filter(op => op.table === 'encounter_creatures');
    const removals = creatureOps.filter(op => op.op === 'delete');
    const retirement = removals.length ? savedCreatureRelocationFrame(envelope, removals, [], computed) : null;
    if (removals.length && !retirement) return null;
    const resourceOps = creatureOps.filter(op => op.op === 'upsert');
    const resourceRows = resourceOps.map(op => ({ ...op, catalog_inputs: undefined }));
    const creatureRows = resourceOps.length ? savedResourceRows(envelope.base, resourceRows, RESOURCES) : [];
    if (!creatureRows) return null;
    const statusOps = writes.filter(op => op.table === 'statuses');
    // Other status carriers retain their existing consumers. A creature caster's
    // status on another target may change its aura inputs and is not a target-only diff.
    for (const op of statusOps) {
        const before = rawMembers.flatMap(row => storedStatusRows(row, 'target')).find(row => statusId(row) === upper(op.row_id));
        const status = op.op === 'delete' ? before : op.cols;
        if (!status || !members.some(row => memberId(row) === upper(readItemField(status, 'status_target_id')))
            || members.some(row => memberId(row) === upper(readStatusCasterId(status))
                && memberId(row) !== upper(readItemField(status, 'status_target_id')))) return null;
    }
    frame.rows = [...creatureRows, ...statusOps.filter(op => op.op === 'upsert').map(op => {
        const saved = { table: op.table, row_id: op.row_id, row: op.cols };
        if (!activityIds.has(statusItem(op.cols))) return saved;
        const id = upper(readItemField(op.cols, 'status_target_id')), member = members.find(row => memberId(row) === id)!;
        const raw = rawMembers.find(row => memberId(row) === id);
        const context = raw?.encounter_context ?? envelope.base.encounter_context ?? envelope.base;
        const dependencies = statusProjectionDependencies(member, id => fromBaseRecord(catalog[id]) ? catalog[id] : null);
        const readers = createStatusLayerReadContext(dependencies);
        // Origin instances are lean; the existing dependency reader merges their held definition underneath them.
        const originId = upper(readItemField(op.cols, 'status_origin_id'));
        const origin = dependencies.resolveInventoryInstance(originId) ?? (fromBaseRecord(catalog[originId]) ? catalog[originId] : {});
        const activity = statusActivityFields(op.cols, origin,
            () => fromBaseRecord(context) && fromBaseRecord(context.campaign_arr) ? context.campaign_arr : null, activityMetadata,
            { layerValue: readers.layerValue, itemName: dependencies.resolveItemName,
                campaignSeconds: time => fromBaseStatusCampaignSeconds(context, time) });
        return { ...saved, node: { ...op.cols, ...activity } };
    })];
    frame.deletes = [...retirement?.deletes ?? [], ...statusOps.filter(op => op.op === 'delete').map(op => ({ table: op.table, row_id: op.row_id }))];
    frame.inputs = { ...frame.inputs, ...retirement?.inputs };
    const projected = applyAppliedFrame(frame, source, false, false);
    if (projected.recover.length || projected.inventoryRecover.length || !fromBaseRecord(projected.value)) return null;
    const nextMembers = [projected.value, ...(Array.isArray(projected.value.encounter_creatures_arr)
        ? projected.value.encounter_creatures_arr.filter(fromBaseRecord) : [])];
    for (const member of members) {
        const id = memberId(member), next = nextMembers.find(row => memberId(row) === id);
        if (!next) {
            if (removals.some(op => upper(op.row_id) === id)) continue;
            return null;
        }
        const before = storedStatusRows(member, 'target'), after = storedStatusRows(next, 'target');
        const steps: MemberStatusStep[] = [];
        const same = (a: Row, b: Row): boolean => statusItem(a) === statusItem(b)
            && upper(readStatusCasterId(a)) === upper(readStatusCasterId(b))
            && Number(readItemField(a, 'status_stack') ?? 1) === Number(readItemField(b, 'status_stack') ?? 1);
        for (const row of before) if (!after.some(next => statusId(next) === statusId(row) && same(row, next)))
            steps.push({ kind: 'remove', statusItemId: statusItem(row), statusId: statusId(row) });
        for (const row of after) if (!before.some(old => statusId(old) === statusId(row) && same(old, row)))
            steps.push({ kind: 'add', statusItemId: statusItem(row), statusId: statusId(row),
                casterId: upper(readStatusCasterId(row)), stack: Number(readItemField(row, 'status_stack') ?? 1) });
        const resource = resourceOps.find(op => upper(op.row_id) === id);
        if (!steps.length && !resource) continue;
        const status = steps.length ? recompileMemberStatusDiff(member, steps,
            { definition: id => fromBaseRecord(catalog[id]) ? catalog[id] : null, catalog, roster: () => members }) : null;
        if (steps.length && !status?.ok) return null;
        // Keep only changed clusters and the root fields their differ names.
        const heldComputed = fromBaseRecord(member.computed) ? member.computed : {}, clusters: Row = {};
        for (const key of ['creature', 'vitality']) if (Object.prototype.hasOwnProperty.call(heldComputed, key)) clusters[key] = heldComputed[key];
        const trial: Row = { encounter_creatures: { ...(member.encounter_creatures as Row) }, items: member.items, // dnr-clone: allow size=one changed EC scalar table row; why=the vitality formula owns its overrides without mutating held saved columns
            computed: clusters, statuses: next.statuses, catalog };
        if (status?.ok) for (const key of [...Object.keys(status.rootScalars), ...Object.keys(status.rootFields ?? {}),
            ...(status.rootBuckets || status.rootFields && Object.keys(status.rootFields).length ? ['item_layer_types'] : [])])
            if (Object.prototype.hasOwnProperty.call(member, key)) trial[key] = member[key];
        const changes = observeActionComputed([[id, trial]]);
        if (status?.ok) {
            Object.assign(trial.computed as Row, { creature: status.creature, vitality: status.vitality });
            applyMemberStatusRoots(trial, status);
        }
        if (resource) {
            const computed = trial.computed as Row;
            if (RESOURCE_COLUMNS.some(key => key !== 'aps' && key in resource.cols))
                computed.vitality = { ...(fromBaseRecord(computed.vitality) ? computed.vitality : {}) };
            const values: ResourceValues = {};
            for (const key of RESOURCE_COLUMNS)
                if (key in resource.cols) values[key] = Number(encounterCreatureField(resource.cols, key));
            applyResourceValues(trial, values);
        }
        const delta = changes();
        // APS is saved; its lean display companion is derived by the resource owner above.
        const used = encounterCreatureField(trial.encounter_creatures, 'aps_used');
        if (resource && 'aps' in resource.cols && used !== encounterCreatureField(member.encounter_creatures, 'aps_used')) {
            const patch = delta[id] ??= { set: [], unset: [] };
            const mirrors = patch.mirrors ??= { set: [], unset: [] };
            const path = ['encounter_creatures', 'aps_used'];
            if (used == null) mirrors.unset.push(path); else mirrors.set.push({ path, value: used });
        }
        Object.assign(frame.computed_delta!, delta);
    }
    return frame;
}
